import React, { useEffect, useRef, useState } from "react";

import HttpManager from "../../services/HttpManager";
import { SaleProduct } from "../../services/v2/sales.service";
import { beep, unlockAudio, useBarcodeCamera } from "../escaner/camara";

/*
	Cámara de Nueva Venta (2026-11-02): como la caja del súper. Queda prendida y cada código (barras o QR) que ve se suma una
	pieza a la venta, con bip, vibración y el marco en verde. El mismo producto vuelve a sumar cuando sale de cuadro y regresa
	(las mismas reglas que el escáner de Conteos). Busca con la búsqueda exacta de Nueva Venta: código del fabricante, código WA
	o SKU; una talla escaneada entra con esa talla.
*/

/** El mismo código a la vista no vuelve a sumar hasta que sale de cuadro este tiempo. */
const SAME_CODE_GAP_MS = 1500;
/** Un código nuevo que aparece mientras otro estaba a la vista es el segundo código de la misma caja: no cuenta. */
const TWO_CODES_MS = 400;

export interface ScanOutcome { ok: boolean; title: string; detail?: string }

interface CamaraVentaProps {
	campusId: number;
	/** Apagada mientras se cobra o ya se cargó la venta. */
	enabled: boolean;
	/** Phone layout: a shorter frame so the cart still shows below. */
	compact: boolean;
	/** Mete el producto a la venta; dice qué pasó (o por qué no se pudo). */
	onProduct: (product: SaleProduct, variantId: number | null) => ScanOutcome;
	/** Código que no está en WAPP: buscar el producto por nombre (y guardarle el código). */
	onNotFound: (code: string) => void;
	onClose: () => void;
}

const CamaraVenta: React.FC<CamaraVentaProps> = ({ campusId, enabled, compact, onProduct, onNotFound, onClose }) => {
	const videoRef = useRef<HTMLVideoElement>(null);
	const seen = useRef<Map<string, number>>(new Map());
	const busyRef = useRef<boolean>(false);
	// Lo que ya se buscó en esta venta: escanear tres piezas iguales no pregunta tres veces al servidor.
	const known = useRef<Map<string, { product: SaleProduct; variantId: number | null } | null>>(new Map());
	const [flash, setFlash] = useState<boolean>(false);
	const [last, setLast] = useState<ScanOutcome | null>(null);
	/** El último código que no estaba en WAPP (para buscarlo y guardárselo al producto). */
	const [missing, setMissing] = useState<string | null>(null);
	const camera = useBarcodeCamera(videoRef, enabled, (codes) => onCodes(codes));

	useEffect(() => { unlockAudio(); }, []);
	useEffect(() => {
		if (!flash) return;
		const timer = setTimeout(() => setFlash(false), 400);
		return () => clearTimeout(timer);
	}, [flash]);

	const find = async (code: string): Promise<{ product: SaleProduct; variantId: number | null } | null> => {
		if (known.current.has(code)) return known.current.get(code) || null;
		const response = await HttpManager.getInstance().salesService.searchProducts(campusId, code, true);
		const hit = response.data.length > 0 ? { product: response.data[0], variantId: response.data[0].scannedVariantId ?? null } : null;
		known.current.set(code, hit);
		return hit;
	};

	const handle = async (code: string) => {
		busyRef.current = true;
		try {
			// The brand's QR on the box is a web address: it is never a product code (Conteos rejects it too).
			if (/^(https?:\/\/|www\.)/i.test(code)) {
				setMissing(null);
				setLast({ ok: false, title: "Ese es el QR de la marca", detail: "Escanea el código de barras del producto." });
				return;
			}
			const hit = await find(code);
			if (!hit) {
				setMissing(code);
				setLast({ ok: false, title: "No está en WAPP", detail: "Código " + code });
				if (navigator.vibrate) navigator.vibrate([40, 60, 40]);
				return;
			}
			const outcome = onProduct(hit.product, hit.variantId);
			setMissing(null);
			setLast(outcome);
			if (outcome.ok) {
				if (navigator.vibrate) navigator.vibrate(60);
				beep();
				setFlash(true);
			}
		} catch (requestError) {
			// A failed search is not "it does not exist": nothing is cached and the code reads again when it comes back.
			setMissing(null);
			setLast({ ok: false, title: "No se pudo buscar", detail: "Revisa la señal y vuelve a escanear." });
		} finally {
			busyRef.current = false;
		}
	};

	const onCodes = (codes: string[]) => {
		const now = Date.now();
		const inFrame = Array.from(new Set(codes.filter(Boolean)));
		if (inFrame.length < 1) return;
		if (inFrame.length > 1) {
			// Two codes in the same frame (EAN + QR on one box): none counts until one is covered.
			inFrame.forEach((code) => { const at = seen.current.get(code); if (at !== undefined && now - at < SAME_CODE_GAP_MS) seen.current.set(code, now); });
			const unread = inFrame.some((code) => { const at = seen.current.get(code); return at === undefined || now - at >= SAME_CODE_GAP_MS; });
			if (unread && !busyRef.current) { setMissing(null); setLast({ ok: false, title: "Hay dos códigos a la vista", detail: "Tapa uno con el dedo." }); }
			return;
		}
		const code = inFrame[0];
		const previous = seen.current.get(code);
		const otherJustSeen = Array.from(seen.current.entries()).some(([other, at]) => other !== code && now - at < TWO_CODES_MS);
		// Every frame renews the mark, even while busy: the same product counts again only after it leaves the frame.
		if (!otherJustSeen) seen.current.set(code, now);
		const fresh = previous === undefined || now - previous >= SAME_CODE_GAP_MS;
		if (!fresh || otherJustSeen || busyRef.current) return;
		handle(code).finally(() => { seen.current.set(code, Date.now()); });
	};

	const hint = camera === "starting" ? "Prendiendo la cámara…"
		: camera === "denied" ? "Sin permiso para la cámara: actívalo en el candado de la barra de direcciones."
		: camera === "unsupported" ? "Este navegador no puede usar la cámara. Usa el buscador o el lector de códigos."
		: "Apunta al código de barras o QR del producto.";

	return <div onPointerDown={unlockAudio} style={{ padding: "0 16px 8px" }}>
		<div style={{ position: "relative", background: "#000", borderRadius: "12px", overflow: "hidden", height: compact ? "190px" : "230px" }}>
			<video ref={videoRef} muted playsInline style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
			<div style={{ position: "absolute", left: "12%", right: "12%", top: "22%", bottom: "22%", border: flash ? "4px solid var(--wa-verde)" : "3px solid rgba(255,255,255,0.85)", borderRadius: "12px", pointerEvents: "none" }} />
			<button type="button" className="btn btn-light btn-sm rounded-pill" style={{ position: "absolute", top: "8px", right: "8px", fontWeight: 600 }} onClick={onClose}>Cerrar Cámara</button>
		</div>
		<div aria-live="polite" style={{ display: "flex", alignItems: "center", gap: "8px", minHeight: "36px", marginTop: "6px" }}>
			<div style={{ flex: 1, minWidth: 0, lineHeight: 1.25 }}>
				{last ? <>
					<div style={{ fontWeight: 700, fontSize: "14px", color: last.ok ? "var(--wa-verde)" : "var(--wa-oro)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{last.ok ? "✓ " : ""}{last.title}</div>
					{last.detail ? <div style={{ fontSize: "12px", color: "var(--wa-apagado)" }}>{last.detail}</div> : null}
				</> : <div style={{ fontSize: "12px", color: "var(--wa-apagado)" }}>{hint}</div>}
			</div>
			{missing ? <button type="button" className="btn btn-outline-secondary btn-sm rounded-pill" style={{ flex: "0 0 auto" }} onClick={() => onNotFound(missing)}>Buscar por Nombre</button> : null}
		</div>
	</div>;
};

export default CamaraVenta;
